import React, { useEffect, useState, useCallback } from 'react';
import { supabase, uploadImage, deleteImageByUrl } from '../../services/supabaseClient';
import Modal from '../Modal';
import ImageUploader from '../ImageUploader';

// Aviso emergente de la portada. Solo se muestra el activo más reciente, así
// que activar uno desactiva automáticamente el resto: tener dos activos a la
// vez no significa nada y llevaría a confusión.
const PopupSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    enlace: '',
    image_url: '',
    activo: true,
  });
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('popup')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      // Antes esto solo iba a consola y la tabla mostraba "sin registros": el
      // mismo aspecto que tener la tabla vacía de verdad. Imposible distinguir
      // un fallo de permisos de una lista vacía.
      console.error(error);
      setLoadError(error.message);
      setItems([]);
    } else {
      setLoadError('');
      setItems(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreateModal = () => {
    setEditing(null);
    setFormData({ titulo: '', enlace: '', image_url: '', activo: true });
    setImageFile(null);
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditing(item);
    setFormData({
      titulo: item.titulo || '',
      enlace: item.enlace || '',
      image_url: item.image_url || '',
      activo: item.activo !== false,
    });
    setImageFile(null);
    setFormError('');
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm('¿Eliminar este aviso?')) return;
    const { error } = await supabase.from('popup').delete().eq('id', item.id);
    if (error) {
      alert('Error: ' + error.message);
      return;
    }
    if (item.image_url) await deleteImageByUrl(item.image_url);
    fetchData();
  };

  // Deja activo solo el indicado; si se pasa null, ninguno queda activo.
  const dejarSoloActivo = async (id) => {
    const apagar = await supabase.from('popup').update({ activo: false }).neq('id', -1);
    if (apagar.error) throw apagar.error;
    if (id) {
      const encender = await supabase
        .from('popup')
        .update({ activo: true, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (encender.error) throw encender.error;
    }
  };

  const activar = async (item) => {
    try {
      await dejarSoloActivo(item.activo ? null : item.id);
      fetchData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    try {
      let imageUrl = formData.image_url;
      if (imageFile) {
        if (editing && editing.image_url) await deleteImageByUrl(editing.image_url);
        imageUrl = await uploadImage(imageFile);
      } else if (imageFile === null && formData.image_url === '') {
        if (editing && editing.image_url) await deleteImageByUrl(editing.image_url);
        imageUrl = null;
      }

      if (!imageUrl) throw new Error('La imagen del aviso es obligatoria');

      const payload = {
        titulo: formData.titulo.trim() || null,
        enlace: formData.enlace.trim() || null,
        image_url: imageUrl,
        activo: formData.activo,
        updated_at: new Date().toISOString(),
      };

      let idGuardado = editing?.id;

      if (editing) {
        const { error } = await supabase.from('popup').update(payload).eq('id', editing.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from('popup').insert([payload]).select('id').single();
        if (error) throw error;
        idGuardado = data.id;
      }

      if (formData.activo && idGuardado) {
        await dejarSoloActivo(idGuardado);
      }

      setShowModal(false);
      fetchData();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const hayActivo = items.some((i) => i.activo);

  return (
    <div>
      <div className="add-button-container">
        <button className="btn btn-primary" onClick={openCreateModal}>
          + Agregar Aviso
        </button>
      </div>

      <p style={{ color: '#666', fontSize: '0.9em', marginBottom: 16 }}>
        {hayActivo
          ? 'El aviso activo se muestra al entrar a la portada del sitio.'
          : 'No hay ningún aviso activo: en la portada no se muestra nada.'}
      </p>

      {loadError && (
        <div className="error-message">
          No se pudieron cargar los datos: {loadError}
        </div>
      )}

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Acciones</th>
                <th>Imagen</th>
                <th>Título</th>
                <th>Enlace</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan="5" className="empty-row">
                    Sin avisos registrados
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="actions-cell">
                        <button className="btn btn-secondary btn-sm" onClick={() => openEditModal(item)}>
                          Editar
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => activar(item)}>
                          {item.activo ? 'Desactivar' : 'Activar'}
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item)}>
                          Eliminar
                        </button>
                      </div>
                    </td>
                    <td>
                      {item.image_url ? (
                        <img src={item.image_url} alt="" className="table-image" loading="lazy" decoding="async" />
                      ) : (
                        <div className="no-image">Sin imagen</div>
                      )}
                    </td>
                    <td>{item.titulo || <span style={{ color: '#999' }}>Sin título</span>}</td>
                    <td>
                      {item.enlace ? (
                        <a href={item.enlace} target="_blank" rel="noreferrer" title={item.enlace}>
                          Ver destino
                        </a>
                      ) : (
                        <span style={{ color: '#999' }}>Sin enlace</span>
                      )}
                    </td>
                    <td>
                      <span style={{ color: item.activo ? '#2d6a2d' : '#999', fontWeight: item.activo ? 700 : 400 }}>
                        {item.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal
          title={editing ? 'Editar Aviso' : 'Agregar Aviso'}
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </>
          }
        >
          <form onSubmit={handleSubmit}>
            {formError && <div className="error-message">{formError}</div>}

            <div className="form-group">
              <label>Título interno</label>
              <input
                type="text"
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                placeholder="Cámara AQP — Junio 2025"
              />
              <small style={{ color: '#666', fontSize: '0.85em' }}>
                Sirve para reconocerlo en esta lista y como texto alternativo de la imagen.
              </small>
            </div>

            <div className="form-group">
              <label>Enlace al pulsar</label>
              <input
                type="url"
                value={formData.enlace}
                onChange={(e) => setFormData({ ...formData, enlace: e.target.value })}
                placeholder="https://www.camara-arequipa.org.pe/revista-institucional"
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={formData.activo}
                  onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                  style={{ width: 'auto' }}
                />
                Mostrar este aviso en la portada
              </label>
              <small style={{ color: '#666', fontSize: '0.85em' }}>
                Al activarlo, cualquier otro aviso queda desactivado.
              </small>
            </div>

            <ImageUploader
              currentImageUrl={formData.image_url}
              onFileSelect={(file) => {
                setImageFile(file);
                if (file === null) setFormData({ ...formData, image_url: '' });
              }}
              label="Imagen del aviso *"
            />
          </form>
        </Modal>
      )}
    </div>
  );
};

export default PopupSection;
